import { DestinationDistance } from "pages/api/distance-search";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";

import {
    DistanceCalculatorWrapper,
    InputsWrapper,
    LoaderWrapper,
    StyledButton,
    StyledDropdown,
    StyledInput
} from "components/vacancies/VacancyOverview/DistanceCalculator/DistanceCalculator.styled";
import { Loader } from "components/shared/loader/Loader";

import { useFilterOptions } from "helpers/hooks/useFilterOptions";

type DistanceCalculatorProps = {
    regions: Array<string>;
    setDestinationsWithDistances: (
        value: Array<DestinationDistance> | undefined
    ) => void;
    /** Boolean to check whether a query has already been made during this components' parents' lifecycle */
    queryHasBeenMade: boolean;
};

const distances = ["5", "10", "20", "30", "50"];

const distanceOptions = distances.map(distance => ({
    label: `< ${distance} km`,
    value: distance
}));

async function distanceMatrixApiCall({
    origin,
    destinations
}: {
    origin: string;
    destinations: Array<string>;
}) {
    const encodedDestinations = encodeURIComponent(destinations.join(","));

    try {
        const response: Array<DestinationDistance> | { error: string } =
            await fetch(
                `/api/distance-search?origins=${origin}&destinations=${encodedDestinations}`
            ).then(res => {
                if (!res.ok) {
                    throw new Error("Error in distance matrix api call");
                }

                return res.json();
            });

        return response;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
    }
}

export function DistanceCalculator({
    regions,
    setDestinationsWithDistances,
    queryHasBeenMade
}: DistanceCalculatorProps) {
    const { filterOptions, setFilterOptions } = useFilterOptions();
    const router = useRouter();

    const [distanceInput, setDistanceInput] = useState(
        distanceOptions.find(
            option => option.value === filterOptions.distanceFilter?.[1]
        ) || distanceOptions[0]
    );
    const [placeInput, setPlaceInput] = useState(
        filterOptions.distanceFilter?.[0] || ""
    );

    const [initialLoad, setInitialLoad] = useState(true);
    const [loading, setLoading] = useState(false);

    const startSearchCallback = useCallback(
        (e?: FormEvent) => {
            e?.preventDefault();
            setLoading(true);

            // Skip if the same search is already active and the input has no value
            if (
                placeInput &&
                (router.query.place !== placeInput ||
                    router.query.distance !== distanceInput.value)
            ) {
                setFilterOptions({
                    ...filterOptions,
                    distanceFilter: [placeInput, distanceInput.value]
                });

                distanceMatrixApiCall({
                    destinations: regions,
                    origin: placeInput
                }).then(result => {
                    setDestinationsWithDistances(
                        result as Array<DestinationDistance>
                    );
                });
            } else {
                setLoading(false);
            }
        },
        [
            distanceInput,
            filterOptions,
            placeInput,
            regions,
            router.query,
            setDestinationsWithDistances,
            setFilterOptions
        ]
    );

    useEffect(() => {
        // Do a query if the user loads the page with a distance and place in the queryparams
        if (
            initialLoad &&
            !queryHasBeenMade &&
            placeInput &&
            distanceInput.value
        ) {
            startSearchCallback();
            setLoading(false);
        }
        setInitialLoad(false);

        function stopLoading() {
            setLoading(false);
        }

        router.events.on("routeChangeComplete", stopLoading);

        return () => {
            router.events.off("routeChangeComplete", stopLoading);
        };
    }, [
        distanceInput,
        initialLoad,
        placeInput,
        queryHasBeenMade,
        router.events,
        startSearchCallback
    ]);

    return (
        <DistanceCalculatorWrapper onSubmit={startSearchCallback}>
            <InputsWrapper>
                <StyledInput
                    label="Postcode of plaats"
                    name="place"
                    placeholder="Bijv. Baarn"
                    value={placeInput}
                    onChange={e => setPlaceInput(e.target.value)}
                    onBlur={startSearchCallback}
                />

                <StyledDropdown
                    label="Afstand"
                    options={distanceOptions}
                    selectedOption={distanceInput}
                    setSelectedOption={value =>
                        setDistanceInput(prev => value ?? prev)
                    }
                    onBlur={startSearchCallback}
                />
            </InputsWrapper>

            <LoaderWrapper loading={loading}>
                <StyledButton
                    type="submit"
                    buttonType="secondary"
                    loading={loading}
                    disabled={loading}
                >
                    Zoek vacatures
                </StyledButton>
                {loading && (
                    <Loader width={24} height={24} color="var(--color-white)" />
                )}
            </LoaderWrapper>
        </DistanceCalculatorWrapper>
    );
}
